import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { FaEye, FaEyeSlash, FaKey } from "react-icons/fa";
import api from "../../api";

export default function SetPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [state, setState] = useState("checking"); // checking | valid | invalid
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) { setState("invalid"); return; }
    let alive = true;
    api.get(`/auth/invite/${encodeURIComponent(token)}`)
      .then((r) => {
        if (!alive) return;
        if (r.data?.valid) { setUsername(r.data.username || ""); setState("valid"); }
        else setState("invalid");
      })
      .catch(() => alive && setState("invalid"));
    return () => { alive = false; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error(t("setPassword.tooShort", "Le mot de passe doit contenir au moins 8 caractères."));
      return;
    }
    if (password !== confirm) {
      toast.error(t("setPassword.mismatch", "Les mots de passe ne correspondent pas."));
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/set-password", { token, password });
      toast.success(t("setPassword.success", "Mot de passe défini. Vous pouvez vous connecter."));
      navigate("/login", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.message || t("setPassword.error", "Échec. Le lien est peut-être expiré."));
      if (err?.response?.status === 400) setState("invalid");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-base-200 px-6 py-12">
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center mb-8 gap-3">
          <div className="w-14 h-14 rounded-xl overflow-hidden border border-base-200 shadow-md bg-base-100">
            <img src="/logo.jpg" alt="The Movers" className="w-full h-full object-contain" />
          </div>
          <h1 className="text-xl font-extrabold text-base-content">The Movers</h1>
        </div>

        <div className="bg-base-100 rounded-2xl border border-base-200 shadow-sm p-8">
          {state === "checking" && (
            <div className="flex items-center justify-center py-8">
              <span className="loading loading-spinner loading-lg text-primary" />
            </div>
          )}

          {state === "invalid" && (
            <div className="text-center space-y-3">
              <div className="w-12 h-12 rounded-2xl bg-error/10 border border-error/20 flex items-center justify-center mx-auto text-error">
                <FaKey />
              </div>
              <h2 className="text-lg font-bold text-base-content">{t("setPassword.invalidTitle", "Lien invalide ou expiré")}</h2>
              <p className="text-sm text-base-content/50">{t("setPassword.invalidDesc", "Demandez une nouvelle invitation à votre administrateur.")}</p>
              <button onClick={() => navigate("/login")} className="btn btn-ghost btn-sm mt-2">{t("setPassword.backLogin", "Retour à la connexion")}</button>
            </div>
          )}

          {state === "valid" && (
            <>
              <div className="mb-6">
                <h2 className="text-xl font-bold text-base-content">{t("setPassword.title", "Définir votre mot de passe")}</h2>
                <p className="text-sm text-base-content/50 mt-1">
                  {t("setPassword.welcome", "Bienvenue")} {username} — {t("setPassword.chooseYours", "choisissez votre mot de passe.")}
                </p>
              </div>

              <form onSubmit={submit} className="flex flex-col gap-4">
                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-semibold text-base-content/80">{t("setPassword.newPassword", "Nouveau mot de passe")}</label>
                  <div className="relative">
                    <input
                      type={showPwd ? "text" : "password"}
                      autoComplete="new-password"
                      className="input input-bordered w-full pr-11 bg-base-200 border-base-200 focus:border-primary focus:outline-none"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      minLength={8}
                    />
                    <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-base-content/50" onClick={() => setShowPwd((v) => !v)} tabIndex={-1}>
                      {showPwd ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-semibold text-base-content/80">{t("setPassword.confirm", "Confirmer le mot de passe")}</label>
                  <input
                    type={showPwd ? "text" : "password"}
                    autoComplete="new-password"
                    className="input input-bordered w-full bg-base-200 border-base-200 focus:border-primary focus:outline-none"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    required
                  />
                </div>

                <button type="submit" disabled={submitting} className="btn btn-primary w-full mt-2 font-semibold">
                  {submitting && <span className="loading loading-spinner loading-sm" />}
                  {t("setPassword.submit", "Valider")}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
